import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import type { Company } from '~/api/auth';
import { useCompany } from '~/api/hooks/useCompany';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { COMPANY_PARAMETER } from '~/routes';

const COMPANY_ID_PATTERN = /^\d+$/;

interface Address {
  pathname: string;
  search: string;
  hash: string;
}

/** O pedido que o endereço trouxe ao abrir a tela. */
interface CompanyRequest {
  /** A empresa que o `?empresa=` pede; `null` quando o valor não é um número. */
  companyId: number | null;
  /** O endereço da chegada sem o parâmetro, com o resto da busca e o `#`. */
  cleanPath: string;
}

function companyIdOf(value: string | null): number | null {
  if (value === null || !COMPANY_ID_PATTERN.test(value)) return null;
  const companyId = Number(value);
  return Number.isSafeInteger(companyId) ? companyId : null;
}

function requestOf({ pathname, search, hash }: Address): CompanyRequest | null {
  const params = new URLSearchParams(search);
  if (!params.has(COMPANY_PARAMETER)) return null;
  const companyId = companyIdOf(params.get(COMPANY_PARAMETER));
  params.delete(COMPANY_PARAMETER);
  const rest = params.toString();
  return { companyId, cleanPath: `${pathname}${rest ? `?${rest}` : ''}${hash}` };
}

/** A empresa do usuário que o pedido nomeia, quando ainda não é a selecionada. */
function companyToSelect(
  companies: Company[],
  selected: Company | undefined,
  request: CompanyRequest | null,
): Company | undefined {
  const companyId = request?.companyId ?? null;
  if (companyId === null || companyId === selected?.id) return undefined;
  return companies.find((company) => company.id === companyId);
}

/**
 * O `?empresa=<id>` com que o push de um post ou de um lembrete abre `/documentos` e `/impostos`
 * (decisão 6 de E16). O endereço é lido uma vez, ao abrir: se o id é de uma empresa do usuário e
 * não é a selecionada, ela passa a ser a selecionada; um id que não é do usuário, ou que não é
 * número, é ignorado. O parâmetro sai do endereço nos dois casos (`replace`), para que voltar ao
 * mesmo endereço depois não troque a empresa que a pessoa escolheu.
 *
 * Devolve `true` enquanto a troca não aconteceu: quem chama não mostra a tela da empresa anterior,
 * que pediria a lista dela e gravaria uma visita que ninguém fez.
 */
export function useCompanyFromUrl(): boolean {
  const location = useLocation();
  const nav = useAppNavigation();
  const { all, selected, select } = useCompany();
  const [request, setRequest] = useState(() => requestOf(location));
  const target = companyToSelect(all, selected, request);

  useEffect(() => {
    if (!request) return;
    if (target) select(target.id);
    nav.replace(request.cleanPath);
    setRequest(null);
  }, [request, target, select, nav]);

  return target !== undefined;
}
